import { useEffect, useMemo, useState, useCallback } from "react";
import PropTypes from "prop-types";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import DataTable from "../../../examples/Tables/DataTable";
import { buildUrl } from "api/base";
import { fetchWithCsrfRetry } from "api/csrf";
import AdminSearch from "./AdminSearch";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

export default function AdminListPage({ title, endpoint }) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await fetch(buildUrl(endpoint), { credentials: "include" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const allRows = data.rows || [];
        if (active) setRows(allRows);
      } catch (e) {
        if (active) setError(e.message || "Ralat memuat data");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [endpoint]);

  /* eslint-disable react/prop-types */
  // Build columns from first row's keys (hide `id`), and support inline edit cells
  const columns = useMemo(() => {
    const first = rows[0];
    if (!first) return [];
    const isDateLike = (val) => {
      if (val instanceof Date) return true;
      if (typeof val !== "string") return false;
      // Detect ISO-like or YYYY-MM-DD HH:MM:SS variants; accept first 10 chars as date
      return /^\d{4}-\d{2}-\d{2}/.test(val);
    };
    const formatDateOnly = (val) => {
      try {
        if (val instanceof Date) return val.toISOString().slice(0, 10);
        if (typeof val === "string" && /^\d{4}-\d{2}-\d{2}/.test(val)) return val.slice(0, 10);
      } catch (_) {}
      return val;
    };
    const renderValue = (v) => (isDateLike(v) ? formatDateOnly(v) : v);

    const keys = Object.keys(first).filter((k) => k.toLowerCase() !== "id");

  const cols = keys.map((k) => ({
      Header: k.toUpperCase(),
      accessor: k,
      // Disable sorting globally (isSorted=false), but keep Cell shaping here
  // react-table provides a Cell renderer with a non-React-prop signature; suppress prop-types lint here
  // eslint-disable-next-line react/prop-types
  Cell: (cell) => {
        const { row, value, column } = cell;
  const original = row?.original || {};
  const rowId = original?.id ?? original?.id_akaun ?? original?.ID ?? null;
        const colKey = column?.id || k;
        const isEditing = editingId !== null && rowId === editingId;
        if (isEditing) {
          const current = draft?.[colKey];
          return (
            <MDInput
              size="small"
              value={current ?? ""}
              fullWidth
              onChange={(e) => setDraft((d) => ({ ...d, [colKey]: e.target.value }))}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  setEditingId(null);
                  setDraft({});
                } else if (e.key === "Enter") {
                  // convenience: save on Enter
                  // We'll trigger save handler via a custom event
                  const btn = document.getElementById(`save-row-${rowId}`);
                  if (btn) btn.click();
                }
              }}
            />
          );
        }
        // Custom styling: highlight invoice (perkara starting with 'INV:') in blue, bold for visibility
        if (k.toLowerCase() === 'perkara' && typeof value === 'string' && value.startsWith('INV:')) {
          return (
            <span style={{ color: '#1565c0', fontWeight: 600 }} title="Rekod Inbois">{renderValue(value)}</span>
          );
        }
        return renderValue(value);
      },
    }));

  // Build actions column (leftmost)
  const actionsCol = {
      Header: "ACTIONS",
      id: "__actions",
      accessor: "__actions",
      disableSortBy: true,
  // eslint-disable-next-line react/prop-types
  Cell: ({ row }) => {
        const original = row?.original || {};
        const rowId = original?.id;
        const isEditing = editingId !== null && rowId === editingId;
        if (isEditing) {
          return (
            <MDBox display="flex" gap={1}>
              <MDButton
                id={`save-row-${rowId}`}
        size="small"
                color="success"
                variant="outlined"
                disabled={saving}
                onClick={async () => {
                  try {
                    setSaving(true);
                    const res = await fetchWithCsrfRetry(buildUrl(`${endpoint}/${rowId}`), {
                      method: "PUT",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({ ...draft, id: rowId }),
                    });
                    if (!res.ok) {
                      let msg = `HTTP ${res.status}`;
                      try { msg += `: ${await res.text()}`; } catch (_) {}
                      throw new Error(msg);
                    }
                    const payload = await res.json();
                    const updated = payload?.row || payload; // accept either shape
                    setRows((prev) => prev.map((r) => (r.id === rowId ? { ...r, ...updated } : r)));
                    setEditingId(null);
                    setDraft({});
                  } catch (e) {
                    console.error("Gagal menyimpan:", e);
                    // minimal UX: just exit saving; keep in edit mode
                    alert(`Gagal menyimpan: ${e.message}`);
                  } finally {
                    setSaving(false);
                  }
                }}
              >
                Simpan
              </MDButton>
              <MDButton
        size="small"
                color="secondary"
                variant="outlined"
                onClick={() => {
                  setEditingId(null);
                  setDraft({});
                }}
              >
                Batal (Esc)
              </MDButton>
            </MDBox>
          );
        }
        return (
          <MDButton
      size="small"
            color="info"
            variant="outlined"
      sx={{ minWidth: "unset", px: 1, py: 0.25, fontSize: "0.7rem", lineHeight: 1.2 }}
            onClick={() => {
              if (rowId == null) {
                console.error("Tiada ID untuk baris ini. Tidak boleh edit.", original);
                alert("Ralat: Tiada ID untuk baris ini. Sila hubungi pentadbir.");
                return;
              }
              setEditingId(rowId);
              setDraft({ ...original });
            }}
          >
            Edit
          </MDButton>
        );
      },
  };

  // Place actions column at the leftmost position
  return [actionsCol, ...cols];
  }, [rows, editingId, draft, saving, endpoint]);
  /* eslint-enable react/prop-types */

  // Prepare filtered rows (search across all columns)
  const filteredRows = useMemo(() => {
    const q = (query || "").trim().toLowerCase();
    if (!q) return rows;
    const safeString = (v) => {
      if (v === null || v === undefined) return "";
      try { return String(v); } catch { return ""; }
    };
    return rows.filter((r) => {
      try {
        return Object.values(r).some((val) => safeString(val).toLowerCase().includes(q));
      } catch {
        return false;
      }
    });
  }, [rows, query]);

  if (loading) return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox p={2}><CircularProgress size={24} /></MDBox>
    </DashboardLayout>
  );
  if (error) return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox p={2}><MDTypography color="error">{error}</MDTypography></MDBox>
    </DashboardLayout>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox p={2}>
        <Card>
          <MDBox p={2}>
            <MDTypography variant="h6">{title}</MDTypography>
            <MDTypography variant="button" color="text">Baca sahaja untuk permulaan</MDTypography>
          </MDBox>
          <MDBox px={1}>
            <AdminSearch value={query} onChange={setQuery} />
            <DataTable
              table={{ columns, rows: filteredRows }}
              isSorted={false}
              entriesPerPage={{ defaultValue: 10, entries: [10, 20, 50] }}
              canSearch={false}
              showTotalEntries
              noPagination={false}
              fontSize={"0.85rem"}
              dense
            />
          </MDBox>
        </Card>
      </MDBox>
    </DashboardLayout>
  );
}

AdminListPage.propTypes = {
  title: PropTypes.string.isRequired,
  endpoint: PropTypes.string.isRequired,
};
